import { Col, Container, Row } from "react-grid-system";
import { StyledBomPage } from "./BomPage.styles";
import { useNavigate, useParams } from "react-router";
import { useStoreActions } from "../../store";
import { useAuth } from "../../utility/useAuth";
import { HeaderMode } from "../../types/HeaderMode";
import { IBomDetails } from "../../types/IBomDetails";
import { useEffect, useState } from "react";
import { getBomDetails, updateBomDetails } from "../../services/ApiService";
import { ToastUtility } from "@syncfusion/ej2-react-notifications";
import { TextBoxComponent } from "@syncfusion/ej2-react-inputs";
import { getValue } from "@syncfusion/ej2-base";
import {
  ColumnDirective,
  ColumnsDirective,
  Grid,
  GridComponent,
  QueryCellInfoEventArgs,
  RowDataBoundEventArgs,
  SelectionSettingsModel,
} from "@syncfusion/ej2-react-grids";
import { IPickedComponent } from "../../types/IPickedComponent";
import dayjs from "dayjs";
import {
  MenuComponent,
  MenuEventArgs,
} from "@syncfusion/ej2-react-navigations";

export const BomPage: React.FC = () => {
  const navigate = useNavigate();
  const { jobNumber } = useParams();
  const { appSettings } = useStoreActions((actions) => actions);
  const auth = useAuth();

  const [bomDetails, setBomDetails] = useState<IBomDetails>();

  function loadBomDetails(jobNumber: string) {
    getBomDetails(auth, jobNumber)
      .then((data) => {
        setBomDetails(data);
      })
      .catch((error) => {
        const message = `Failed to get Bom details. Error: ${error}`;
        console.error(`Failed to get Bom details. Error: ${error}`);
        ToastUtility.show(message, "Error", 2000);
      });
  }

  useEffect(() => {
    if (jobNumber) {
      appSettings.setJobNumber(jobNumber);
      appSettings.setHeaderMode(HeaderMode.Bom);
      loadBomDetails(jobNumber);
    }
  }, [jobNumber]);

  let selIndex: number[] = [];
  let gridPickedComponents: Grid | null;
  const settings: SelectionSettingsModel = { type: "Multiple" };
  const rowDataBound = (args: RowDataBoundEventArgs): void => {
    if (getValue("checked", args.data as object)?.length > 0) {
      selIndex.push(
        parseInt(
          (args.row as HTMLTableRowElement).getAttribute(
            "aria-rowindex",
          ) as string,
          0,
        ) - 1,
      );
    }
  };
  const dataBound = (): void => {
    if (gridPickedComponents && selIndex.length) {
      gridPickedComponents.selectRows(selIndex);
      selIndex = [];
    }
  };

  const gridTemplate = (props: IPickedComponent) => {
    if (props.checked && props.dateUpdated) {
      return `${props.checked} ${dayjs(props.dateUpdated).format("DD/MM/YYYY HH:mm:ss")}`;
    } else {
      return "";
    }
  };

  function selectMenuItem(args: MenuEventArgs): void {
    if (args.item.id === "updateBom" && jobNumber) {
      const components = (
        gridPickedComponents as GridComponent
      ).getSelectedRecords() as IPickedComponent[];
      updateBomDetails(auth, jobNumber, components)
        .then(() => {
          loadBomDetails(jobNumber);
          ToastUtility.show(`Succesfully updated.`, "Info", 2000);
        })
        .catch((error) => {
          const message = `Failed to update Bom details. Error: ${error}`;
          console.error(`Failed to update Bom details. Error: ${error}`);
          ToastUtility.show(message, "Error", 2000);
        });
    } else if (args.item.id === "back") {
      navigate(-1);
    }
  }

  const customizePickedComponentCell = (args: QueryCellInfoEventArgs) => {
    if (args.cell && args.data) {
      const pickedComponent = args.data as IPickedComponent;

      if (pickedComponent.qtyOrdered > pickedComponent.qtyPicked) {
        args.cell.classList.add("pickedComponent-partial");
      } else {
        args.cell.classList.add("pickedComponent-ok");
      }
    }
  };

  return (
    <StyledBomPage>
      {bomDetails ? (
        <Container fluid>
          <Row align="stretch">
            <MenuComponent
              items={[
                { id: "back", text: "Back", iconCss: "e-icons e-back" },
                { id: "updateBom", text: "Update", iconCss: "e-icons e-save" },
              ]}
              select={selectMenuItem}
            />
          </Row>
          <Row align="stretch">
            <Col>Stock Code:</Col>
            <Col>
              <TextBoxComponent value={bomDetails.job.customerCode} readOnly />
            </Col>
            <Col>Description:</Col>
            <Col>
              <TextBoxComponent
                value={bomDetails.job.description}
                readOnly
                multiline
              />
            </Col>
            <Col>Job #:</Col>
            <Col>
              <TextBoxComponent
                value={bomDetails.job.qtyOrdered.toString()}
                readOnly
              />
            </Col>
          </Row>
          <Row align="stretch">
            <Col>Quantity Ordered:</Col>
            <Col>
              <TextBoxComponent
                value={bomDetails.job.qtyOrdered.toString()}
                readOnly
              />
            </Col>
            <Col>Customer:</Col>
            <Col>
              <TextBoxComponent value={bomDetails.job.customerCode} readOnly />
            </Col>
            <Col>Order #:</Col>
            <Col>
              <TextBoxComponent value={bomDetails.job.orderNumber} readOnly />
            </Col>
          </Row>
          <Row align="stretch">
            <Col>Quantity Built:</Col>
            <Col>
              <TextBoxComponent
                value={bomDetails.job.qtyBuilt.toString()}
                readOnly
              />
            </Col>
            <Col>Customer:</Col>
            <Col>
              <TextBoxComponent value={bomDetails.job.customerName} readOnly />
            </Col>
            <Col>Customer PO #:</Col>
            <Col>
              <TextBoxComponent
                value={bomDetails.job.customerPONumber}
                readOnly
              />
            </Col>
          </Row>
          <Row align="stretch">
            <GridComponent
              ref={(g: GridComponent) => (gridPickedComponents = g)}
              queryCellInfo={customizePickedComponentCell}
              rowDataBound={rowDataBound}
              dataBound={dataBound}
              selectionSettings={settings}
              gridLines="Both"
              dataSource={bomDetails.pickedComponents}
              allowTextWrap={true}
            >
              <ColumnsDirective>
                <ColumnDirective type="checkbox" width="50" />
                <ColumnDirective
                  field="lineNum"
                  headerText="Line #"
                  width="100"
                  textAlign="Center"
                />
                <ColumnDirective
                  field="componentPart" 
                  headerText="Part Number"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="qtyOrdered"
                  headerText="Qty Ordered"
                  width="100"
                  textAlign="Center"
                />
                <ColumnDirective
                  field="qtyPicked"
                  headerText="Qty Picked"
                  width="100"
                  textAlign="Center"
                />
                <ColumnDirective
                  field="qtyRemaining"
                  headerText="Qty Remaining"
                  width="100"
                  textAlign="Center"
                />
                <ColumnDirective
                  field="issueNo"
                  headerText="Issue No"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="description"
                  headerText="Description"
                  textAlign="Left"
                />
                <ColumnDirective
                  template={gridTemplate}
                  headerText="Checked"
                  textAlign="Left"
                />
              </ColumnsDirective>
            </GridComponent>
          </Row>
        </Container>
      ) : (
        <>Loading...</>
      )}
    </StyledBomPage>
  );
};
